import ENDPOINTS from '.';

import config from '@/config';
import {
  pointsLeaderboardResponseSchema,
  PointsLeaderboardResponse,
  RankingType,
} from '@/types/schemas';

const BASE_URL = config.API_BASE_URL;

export type TimeRange = 'all' | '30d' | '7d' | '24h';

interface GetLeaderboardParams {
  range?: TimeRange;
  page?: number;
  limit?: number;
  type?: RankingType;
}

export async function getPointsLeaderboard(
  params: GetLeaderboardParams = {}
): Promise<PointsLeaderboardResponse> {
  // Only include type parameter if feature flag is enabled
  const requestBody: Record<string, any> = {
    range: params.range ?? 'all',
    page: params.page ?? 1,
    limit: params.limit ?? 10,
    type: params.type ?? 'burned',
  };

  // Only add type if new leaderboards feature is enabled
  if (config.ENABLE_NEW_LEADERBOARDS) {
    requestBody.type = params.type ?? 'burned';
  }

  const response = await fetch(ENDPOINTS.pointsLeaderboardList, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(requestBody),
  });

  const json = await response.json();

  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  // Backend returns {code: 0, data: {...}}, extract data
  const responseData = json.data || json;

  // Add default type if not present (backward compatibility with old backend)
  if (!responseData.type) {
    responseData.type = params.type ?? 'unused';
  }

  return pointsLeaderboardResponseSchema.parse(responseData);
}

interface GetMyRankParams {
  range?: TimeRange;
  type?: RankingType;
}

export async function getMyRank(params: GetMyRankParams = {}) {
  const url = new URL(ENDPOINTS.pointsLeaderboardMyRank, BASE_URL);
  if (params.range) {
    url.searchParams.set('range', params.range);
  }
  // Only add type parameter if new leaderboards feature is enabled
  if (config.ENABLE_NEW_LEADERBOARDS && params.type) {
    url.searchParams.set('type', params.type);
  }

  const response = await fetch(url.toString(), {
    credentials: 'include',
  });

  const json = await response.json();

  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  return json.data || json;
}
